import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { Table, Drawer, Button, notification } from 'antd';
import { useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';

// Components
import TableToolbar from './TableToolbar';
import TableFilters from './TableFilters';
import TableBulkActions from './TableBulkActions';
import TableColumnManager from './TableColumnManager';
import TableSummary from './TableSummary';

// Enhanced Hooks
import { useAdvancedTable, useRowSelection, useColumnVisibility } from './hooks/useAdvancedTable';
import { useTableExport } from './hooks/useTableExport';

// Utils
import { calculateSummary } from './utils/tableHelpers';

// Styles
import styles from './styles/AdvancedTable.module.css';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const AdvancedTable = memo(({
  // Required
  resource,
  columns = [],
  fetchData,
  data: externalData = null,
  loading: externalLoading = false,

  // Features
  enableSearch = true,
  enableFilters = false,
  enableRefresh = false,
  enableExport = true,
  enableBulkActions = false,
  enableColumnManager = false,
  enableSummary = false,
  enableSelection = false,
  enableHorizontalScroll = false,

  // Configuration
  filterConfig = [],
  bulkActions = [],
  summaryConfig = [],
  defaultPageSize = 10,
  defaultSort = null,
  defaultFilters = {},
  tableId,

  // Callbacks
  onRowClick,
  onRowDoubleClick,
  onSelectionChange,
  onDataLoaded,

  // Styling
  density: initialDensity = 'comfortable',
  stickyHeader = false,
  striped = true,
  enableHorizontalScrollFeature = false,
  highlightOnHover = true,

  // Advanced
  exportFileName = 'export',
  enableToolBar = true,
  persistState = false,
  enableDensity = false,
  scroll = {},
  customActions = null,
}) => {
  // Redux
  const { userDetails } = useSelector(state => state?.auth);

  // Enhanced table state management
  const tableState = useAdvancedTable({
    defaultPage: 1,
    defaultPerPage: defaultPageSize,
    defaultSort,
    defaultFilters,
    tableId: tableId || resource,
    persistState,
  });

  // Row selection management
  const rowSelection = useRowSelection();

  // Column visibility management
  const columnVisibility = useColumnVisibility(
    columns,
    tableId || resource,
    persistState
  );

  // Export functionality
  const exportHook = useTableExport();

  // Local state
  const [data, setData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [showFilters, setShowFilters] = useState(!!filterConfig.length);
  const [density, setDensity] = useState(initialDensity);
  const [horizontalScrollEnabled, setHorizontalScrollEnabled] = useState(enableHorizontalScroll);
  const [allData, setAllData] = useState([]); // Store all data for client-side pagination
  const [useClientSidePagination, setUseClientSidePagination] = useState(false);

  // Memoized values
  const memoizedFilterConfig = useMemo(() => filterConfig, [JSON.stringify(filterConfig)]);

  const permittedColumns = useMemo(() => {
    const visibleCols = columnVisibility.getVisibleColumns();

    return visibleCols.map(col => {
      // Calculate minimum width based on column type and content
      const getColumnWidth = (column) => {
        if (column.width) return column.width;

        // Set widths based on column type/content
        const nameLength = column.name.length;
        const selector = typeof column.selector === 'string' ? column.selector : '';

        if (column.id === 'actions') return 150;
        if (selector === 'email') return Math.max(200, nameLength * 10);
        if (selector === 'name' || selector === 'title') return Math.max(180, nameLength * 8);
        if (selector === 'status') return Math.max(100, nameLength * 8);
        if (selector === 'date' || selector.includes('Date')) return 140;
        if (selector === 'phone') return 150;
        if (selector === 'address') return 200;

        // Default minimum width
        return Math.max(120, nameLength * 8);
      };

      return {
        title: col.name,
        dataIndex: typeof col.selector === 'string' ? col.selector : undefined,
        key: col.id || col.name,
        render: (text, record, index) => {
          let value = text;
          if (col.cell) {
            value = col.cell(record, index);
          } else if (typeof col.selector === 'function') {
            value = col.selector(record);
          }

          return value === null || value === undefined ? '' : value;
        },
        sorter: col.sortable ? true : false,
        width: getColumnWidth(col),
        minWidth: getColumnWidth(col), // Use rc-table's built-in minWidth
        ellipsis: {
          showTitle: true, // Show tooltip on hover for long content
        },
        fixed: col.fixed || undefined, // Support column pinning
        align: col.align || undefined,
      };
    });
  }, [columnVisibility, columns]);

  const summary = useMemo(() => {
    if (!enableSummary || !summaryConfig.length) return [];
    const result = calculateSummary(data, summaryConfig);
    return Array.isArray(result) ? result : [];
  }, [data, summaryConfig, enableSummary]);

  // Load data function
  const loadData = useCallback(async () => {
    if (!fetchData) return;

    setLoading(true);
    try {
      // Check if filters are active
      const hasActiveFilters = Object.values(tableState.filters).some(value => {
        if (value === null || value === undefined || value === '') return false;
        if (Array.isArray(value)) return value.length > 0;
        return true;
      });

      const params = {
        page: tableState.page,
        perPage: tableState.perPage,
        ...(tableState.sort && {
          sortField: tableState.sort.field,
          sortOrder: tableState.sort.order
        }),
        ...(tableState.debouncedSearch && { search: tableState.debouncedSearch }),
        ...Object.entries(tableState.filters).reduce((acc, [key, value]) => {
          if (value === null || value === undefined || value === '') return acc;

          if (Array.isArray(value)) {
            acc[key] = value.join(',');
          } else if (typeof value === 'object' && value.start && value.end) {
            acc[`${key}_start`] = value.start;
            acc[`${key}_end`] = value.end;
          } else {
            acc[key] = value;
          }
          return acc;
        }, {}),
      };

      const result = await fetchData(params);
      const fetchedData = result.data || [];
      const fetchedTotal = result.total || 0;

      // If filters are active, we just show what we fetched without client-side pagination logic
      // tailored for standard pagination scenarios
      if (hasActiveFilters) {
        setData(fetchedData);
        setTotalRows(fetchedTotal);
        setAllData([]);
        setUseClientSidePagination(false);
      } else if (fetchedData.length === fetchedTotal && fetchedTotal > 0) {
        // If we got all data (small dataset), enable client-side pagination
        setAllData(fetchedData);
        setUseClientSidePagination(true);
        // For client-side pagination, show current page's data
        const startIndex = (tableState.page - 1) * tableState.perPage;
        const endIndex = startIndex + tableState.perPage;
        setData(fetchedData.slice(startIndex, endIndex));
        setTotalRows(fetchedTotal);
      } else {
        setAllData([]);
        setUseClientSidePagination(false);
        setData(fetchedData);
        setTotalRows(fetchedTotal);
      }

      onDataLoaded?.(result);
    } catch (error) {
      console.error('Error loading table data:', error);
      notification.error({
        message: 'Error Loading Data',
        description: 'Failed to load table data. Please try again.',
      });
      setData([]);
      setTotalRows(0);
      setAllData([]);
      setUseClientSidePagination(false);
    } finally {
      setLoading(false);
    }
  }, [
    fetchData,
    tableState.page,
    tableState.perPage,
    tableState.sort,
    tableState.debouncedSearch,
    tableState.filters,
    onDataLoaded
  ]);

  // Handle external data (for client-side mode)
  useEffect(() => {
    if (externalData !== null) {
      setData(externalData);
      setTotalRows(externalData.length);
    }
  }, [externalData]);

  // Handle client-side pagination
  useEffect(() => {
    if (useClientSidePagination && allData.length > 0 && !tableState.hasActiveFilters) {
      const startIndex = (tableState.page - 1) * tableState.perPage;
      const endIndex = startIndex + tableState.perPage;
      setData(allData.slice(startIndex, endIndex));
    }
  }, [useClientSidePagination, allData, tableState.page, tableState.perPage, tableState.hasActiveFilters]);

  // Effects
  useEffect(() => {
    // Only load data if fetchData is provided and no external data
    if (fetchData && externalData === null) {
      loadData();
    }
  }, [loadData, fetchData, externalData]);

  useEffect(() => {
    onSelectionChange?.(rowSelection.selectedRows);
  }, [rowSelection.selectedRows, onSelectionChange]);

  // Handlers
  const handleTableChange = useCallback((pagination, filters, sorter) => {
    // Only handle sorting here since pagination is handled directly in pagination config
    if (sorter.field) {
      const originalCol = columns.find(c => (c.id || c.name) === sorter.columnKey);
      const field = originalCol?.selector || sorter.field;
      tableState.setSort({ field, order: sorter.order === 'ascend' ? 'asc' : 'desc' });
    } else if (sorter.order === undefined) {
      // Clear sorting when no order is specified
      tableState.setSort(null);
    }
  }, [columns, tableState]);

  const handleExport = useCallback(async (type) => {
    const visibleColumns = columnVisibility.getVisibleColumns();
    const exportData = rowSelection.selectedRows.length > 0 ? rowSelection.selectedRows : data;

    try {
      const result = await exportHook.exportData(type, exportData, visibleColumns, exportFileName, {
        title: `${resource} Export`,
        orientation: 'landscape',
        sheetName: resource,
      });

      if (result.success) {
        notification.success({
          message: 'Export Successful',
          description: `Data exported as ${type.toUpperCase()} successfully.`,
        });
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Export error:', error);
      notification.error({
        message: 'Export Failed',
        description: error.message || 'Failed to export data. Please try again.',
      });
    }
  }, [data, rowSelection.selectedRows, columnVisibility, exportHook, exportFileName, resource]);

  const handleToggleHorizontalScroll = useCallback(() => {
    setHorizontalScrollEnabled(prev => !prev);
  }, []);

  // Check permissions
  const canView = userDetails?.permissions?.[resource]?.view !== false;
  const canExport = userDetails?.permissions?.[resource]?.export !== false;

  if (!canView) {
    return (
      <div className={styles.advancedTable}>
        <div style={{ padding: 24, textAlign: 'center' }}>
          <FeatherIcon icon="lock" size={48} color="#ccc" />
          <h3>Access Denied</h3>
          <p>You don&apos;t have permission to view this data.</p>
        </div>
      </div>
    );
  }

  const emptyComponent = (
    <div className={styles.emptyState}>
      <FeatherIcon icon="inbox" size={48} />
      <h3>No data found</h3>
      <p>
        {tableState.hasActiveFilters
          ? 'Try adjusting your filters'
          : 'There are no records to display'
        }
      </p>
    </div>
  );

  return (
    <div className={styles.advancedTable}>
      {/* Summary */}
      {enableSummary && <TableSummary stats={summary} loading={loading} />}

      {/* Toolbar */}
      {enableToolBar && <TableToolbar
        searchValue={tableState.search}
        onSearchChange={tableState.setSearch}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        onExport={handleExport}
        onRefresh={loadData}
        onDensityChange={setDensity}
        density={density}
        enableSearch={enableSearch}
        enableDensity={enableDensity}
        enableFilters={enableFilters}
        enableRefresh={enableRefresh}
        enableExport={enableExport && canExport}
        enableColumnManager={enableColumnManager}
        enableHorizontalScrollFeature={enableHorizontalScrollFeature}
        activeFilterCount={tableState.activeFilterCount}
        customActions={customActions}
        onColumnManagerClick={() => setShowColumnManager(true)}
        exportLoading={exportHook.exportLoading}
        enableHorizontalScroll={horizontalScrollEnabled}
        onToggleHorizontalScroll={handleToggleHorizontalScroll}
      />}

      {/* Filters */}
      {enableFilters && showFilters && (
        <TableFilters
          filterConfig={memoizedFilterConfig}
          filters={tableState.filters}
          onChange={tableState.updateFilter}
          onReset={tableState.resetFilters}
        />
      )}

      {/* Active Filters Indicator */}
      {tableState.hasActiveFilters && (
        <div style={{
          padding: '8px 16px',
          borderBottom: '1px solid #f0f0f0',
          background: '#fafafa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>
            <FeatherIcon icon="filter" size={14} style={{ marginRight: 8 }} />
            {tableState.activeFilterCount} filter(s) applied
          </span>
          <Button size="small" onClick={tableState.resetFilters}>
            Clear All Filters
          </Button>
        </div>
      )}

      {/* Bulk Actions */}
      {enableBulkActions && rowSelection.selectedCount > 0 && (
        <TableBulkActions
          selectedRows={rowSelection.selectedRows}
          actions={bulkActions}
          onClearSelection={rowSelection.clearSelection}
        />
      )}

      {/* Table */}
      <div className={`${styles.tableContainer} ${stickyHeader ? styles.stickyHeader : ''} ${styles[density]}`}>
        <Table
          columns={permittedColumns}
          dataSource={data}
          loading={loading || externalLoading}
          rowKey={record => record._id || record.id || record.key}
          bordered
          tableLayout="fixed"
          pagination={{
            current: tableState.page,
            pageSize: tableState.perPage,
            total: totalRows,
            showSizeChanger: true,
            pageSizeOptions: ROWS_PER_PAGE_OPTIONS,
            showQuickJumper: totalRows > 500,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            position: ['bottomRight'],
            onChange: (page, pageSize) => {
              tableState.setPage(page);
              const numPageSize = parseInt(pageSize, 10);
              if (numPageSize !== tableState.perPage) {
                tableState.setPerPage(numPageSize);
              }
            },
            onShowSizeChange: (current, size) => {
              tableState.setPerPage(parseInt(size, 10));
            },
          }}
          onChange={handleTableChange}
          rowSelection={
            (enableSelection || enableBulkActions)
              ? {
                selectedRowKeys: rowSelection.selectedRowKeys,
                onChange: rowSelection.handleRowSelect,
                getCheckboxProps: (record) => ({
                  name: record.name,
                }),
                fixed: true,
                columnWidth: 50,
              }
              : undefined
          }
          onRow={(record) => ({
            onClick: () => onRowClick && onRowClick(record),
            onDoubleClick: () => onRowDoubleClick && onRowDoubleClick(record),
            className: highlightOnHover ? styles.hoverableRow : '',
          })}
          size={density === 'compact' ? 'small' : density === 'spacious' ? 'large' : 'middle'}
          scroll={{
            x: horizontalScrollEnabled ? true : false,
            y: stickyHeader ? 500 : undefined
          }}
          locale={{ emptyText: emptyComponent }}
          className={striped ? styles.stripedTable : ''}
          sticky={stickyHeader ? { offsetHeader: 0 } : false}
        />
      </div>

      {/* Column Manager */}
      <Drawer
        title="Manage Columns"
        placement="right"
        onClose={() => setShowColumnManager(false)}
        open={showColumnManager}
        width={350}
        zIndex={9999}
        styles={{
          body: { padding: 0, height: '100%' }
        }}
        height="100vh"
      >
        <TableColumnManager
          columns={columns}
          visibleColumns={columnVisibility.visibleColumns}
          onToggleColumn={columnVisibility.toggleColumn}
          onShowAll={columnVisibility.showAll}
          onHideAll={columnVisibility.hideAll}
          onReset={columnVisibility.resetToDefault}
        />
      </Drawer>
    </div>
  );
});

AdvancedTable.displayName = 'AdvancedTable';

AdvancedTable.propTypes = {
  // Required
  resource: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  fetchData: PropTypes.func,
  data: PropTypes.array,
  loading: PropTypes.bool,

  // Features
  enableSearch: PropTypes.bool,
  enableFilters: PropTypes.bool,
  enableExport: PropTypes.bool,
  enableBulkActions: PropTypes.bool,
  enableColumnManager: PropTypes.bool,
  enableRefresh: PropTypes.bool,
  enableSummary: PropTypes.bool,
  enableSelection: PropTypes.bool,
  enableToolBar: PropTypes.bool,
  enableHorizontalScroll: PropTypes.bool,

  // Configuration
  filterConfig: PropTypes.array,
  bulkActions: PropTypes.array,
  summaryConfig: PropTypes.array,
  defaultPageSize: PropTypes.number,
  defaultSort: PropTypes.object,
  defaultFilters: PropTypes.object,
  tableId: PropTypes.string,

  // Callbacks
  onRowClick: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  onSelectionChange: PropTypes.func,
  onDataLoaded: PropTypes.func,

  // Styling
  density: PropTypes.oneOf(['compact', 'comfortable', 'spacious']),
  stickyHeader: PropTypes.bool,
  striped: PropTypes.bool,
  highlightOnHover: PropTypes.bool,

  // Advanced
  exportFileName: PropTypes.string,
  serverSide: PropTypes.bool,
  persistState: PropTypes.bool,
  scroll: PropTypes.object,
  customActions: PropTypes.node,
};

export default AdvancedTable;
